/**
 * RIG page back-navigation (R-6/D).
 *
 * The RIG routed page (/rig?signal=<id>) is reached from the Signal modal's
 * launch button and returns to that signal's detail. Two affordances share this
 * one destination: the prominent "Back to signal" control at top-left, and a
 * click on genuinely inert page background.
 *
 * This module is the single source of truth for BOTH — the destination and the
 * rule deciding whether a background click counts — so the two paths cannot
 * drift apart. It is deliberately free of React and DOM globals so the
 * no-misfire contract can be asserted directly.
 *
 * NOTE this concerns the routed RIG PAGE only. RkgModal is a different surface
 * and is not touched.
 */

/** Where the RIG returns to. null when the page was opened without a signal. */
export function rigBackTarget(signalId: string | null | undefined): string | null {
  if (!signalId) return null;
  return `/signals/${signalId}`;
}

/**
 * The subset of a click event the no-misfire rule reads. Structural, so tests
 * can construct cases without a DOM.
 */
export interface BackgroundClickLike {
  /** The deepest element the click landed on. */
  target: unknown;
  /** The element the handler is bound to — the RIG page root. */
  currentTarget: unknown;
  /** 0 = primary. */
  button: number;
  /** Click count; 2+ means a double/triple click, which selects text. */
  detail: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/**
 * True only for a click on genuinely inert page background.
 *
 * The load-bearing rule is `target === currentTarget`: the handler is bound to
 * the RIG page root, so this fires ONLY when the click landed on that element's
 * own box — its padding band and the gaps between its section children. Every
 * descendant is excluded structurally rather than by enumeration, which is what
 * makes the no-misfire contract hold for the graph canvas and all its
 * interactions (drag, pan, zoom, node and edge clicks), the header card, the
 * density and toggle controls, the relationship reads, the cross-practice
 * delivery cards, the pattern read, the warm paths, and every other interactive
 * element — including any added later. The left navigation rail sits outside the
 * page root entirely (it is a sibling of <main>), so it can never reach here.
 *
 * The remaining guards keep the affordance from stealing ordinary interactions:
 *   - non-primary buttons (middle-click paste, right-click menu) are ignored;
 *   - a modifier key means the user is opening a new tab or extending a
 *     selection, not navigating;
 *   - a double or triple click is a text selection gesture;
 *   - a click that ends a drag-selection must not navigate, so a live
 *     non-collapsed selection suppresses it;
 *   - the press must have STARTED on the background too. A browser dispatches
 *     `click` on the nearest common ancestor of mousedown and mouseup, so a drag
 *     that begins inside the graph and releases over the background would
 *     otherwise arrive here with target === the page root. Requiring the press
 *     to have started on the background closes that hole for every drag, pan or
 *     zoom gesture — including ones the graph does not implement yet.
 */
export function isInertBackgroundClick(
  event: BackgroundClickLike,
  options: { hasTextSelection?: boolean; pressStartedOnBackground?: boolean } = {},
): boolean {
  if (event.target !== event.currentTarget) return false;
  if (options.pressStartedOnBackground === false) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (event.detail > 1) return false;
  if (options.hasTextSelection) return false;
  return true;
}
